import { service } from '@ember/service';

import type { PopupListItem, Service } from 'webapp-types';
import { Direction } from 'webapp/enums/enums';

import PopupListComponent from 'webapp/components/common/popups/popup-list';

import type { PopupContextProps } from 'webapp/models-custom/popup/context';
import PopupContext from 'webapp/models-custom/popup/context';

type PopupListProps = Omit<PopupContextProps, 'directions' | 'component'>;

export default class PopupList extends PopupContext {
	@service('popup') declare popupService: Service.Popup;

	parentPopup: PopupList | null = null;
	childPopup: PopupList | null = null;

	private static sublistCloseTimeoutId: number | null = null;

	constructor(props: PopupListProps) {
		super({
			...props,
			component: PopupListComponent,
		});

		const isInstanceOpen = this.popupService.isOpen(PopupListComponent);

		this.directions = isInstanceOpen
			? [Direction.RIGHT, Direction.LEFT]
			: [Direction.DOWN, Direction.UP];
	}

	setChild(popup: PopupList): void {
		this.closeChild();
		this.childPopup = popup;
	}

	override close(): Promise<unknown> {
		this.closeChild();
		return super.close();
	}

	closeChild(): void {
		// closing of child popups children is handled by the popupService removePopup() method
		if (this.childPopup) {
			void this.childPopup.close();
			this.childPopup = null;
		}
	}

	startCloseTimer(): void {
		this.cancelCloseTimer();

		PopupList.sublistCloseTimeoutId = window.setTimeout(() => {
			this.findRootPopup().closeChild();
			PopupList.sublistCloseTimeoutId = null;
		}, 300);
	}

	cancelCloseTimer(): void {
		if (PopupList.sublistCloseTimeoutId) {
			window.clearTimeout(PopupList.sublistCloseTimeoutId);
			PopupList.sublistCloseTimeoutId = null;
		}
	}

	openSublist(
		element: HTMLElement,
		sublistItems: PopupListItem<string>[],
		onAction: (item: PopupListItem<string>) => void,
		sublistTitle?: string,
	): PopupList | void {
		const popup = this.popupService.createListPopup(
			element,
			'',
			'optionspopup sublist-popup',
			true,
			true,
		);

		if (popup) {
			popup.parentPopup = this;
			this.setChild(popup);

			popup.open({
				title: sublistTitle,
				itemsList: sublistItems,
				doAction: (actionName?: string, ...args: unknown[]) => {
					void popup.close();
					if (actionName) {
						onAction({
							id: args[0] as string | undefined,
							action: actionName,
						} as PopupListItem<string>);
					}
				},
			});

			return popup;
		}
	}

	shouldOpenNewSublist(element: HTMLElement): boolean {
		return this.childPopup?.attachTo !== element;
	}

	private findRootPopup(): PopupList {
		return this.parentPopup?.findRootPopup() || this;
	}
}
